import { faCheck, faDatabase, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Badge, Blockquote, InfoTooltip } from '@ui';
import useCheckPermissions from 'hooks/useCheckPermissions';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useModal } from '../../../../hooks/useModal';
import { useClubSlug } from '../../../Clubs/hooks';
import { TableActionButton } from '../../../DataTable';
import Loader from '../../../Loader';
import { ApplicationResponseAction, ApplicationResponseForm } from '../../ApplicationList';
import ApplicationStatusBadge from '../../ApplicationList/ApplicationStatusBadge';
import { ClubMember, MemberApplication } from '../../types';

const FormDataPreview = dynamic(() => import('../../../Formular/FormDataPreview'));

interface Props {
    member: ClubMember;
}

/**
 * The membership applications of one member: the one they were accepted from, plus anything
 * still open or declined that the same account or email address sent in.
 */
const ClubMemberApplications = ({ member }: Props) => {
    const { t } = useTranslation('member-management');
    const clubSlug = useClubSlug();
    const [setModal, resetModal] = useModal();
    const { data, isLoading, mutate } = useSWR<{ data: MemberApplication[] }>(
        `club-member/${member.id}/applications`
    );
    const { hasAccess: canRespond } = useCheckPermissions({
        clubSlug,
        permission: 'member_management.application.update'
    });

    const applications = data?.data ?? [];

    const showFormDataPreview = (application: MemberApplication) =>
        setModal({
            title: t('applications.list.formDataModalTitle', { name: application.user_name }),
            content: <FormDataPreview formDataId={application.form_data_id} />,
            show: true,
            size: 60
        });

    const showResponseForm = (application: MemberApplication, action: ApplicationResponseAction) =>
        setModal({
            title:
                action === 'accept'
                    ? t('applications.list.acceptModalTitle', { name: application.user_name })
                    : t('applications.list.declineModalTitle', { name: application.user_name }),
            content: (
                <ApplicationResponseForm
                    clubSlug={clubSlug}
                    action={action}
                    application={application}
                    callback={() => {
                        void mutate();
                        resetModal();
                    }}
                />
            ),
            show: true
        });

    return (
        <Loader isLoading={isLoading}>
            {applications.length === 0 ? (
                <Blockquote>{t('clubMember.preview.applications.empty')}</Blockquote>
            ) : (
                <div className="tw-flex tw-flex-col tw-gap-2">
                    {applications.map((application) => (
                        <div className="tw-card-flat tw-flex tw-items-center tw-gap-3 !tw-p-3" key={application.id}>
                            <div className="tw-shrink-0">
                                <ApplicationStatusBadge status={application.status} size="sm" />
                            </div>

                            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                                <span className="tw-truncate tw-font-bold">{application.user_name}</span>
                                <span className="tw-truncate tw-text-xs tw-text-accents-6">
                                    {t('clubMember.preview.applications.submittedAt', {
                                        date: moment(application.created_at).format('L LT')
                                    })}
                                    {application.user_email ? ` · ${application.user_email}` : ''}
                                </span>
                                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                                    {application.family_token && (
                                        <Badge flat size="xs" color="secondary">
                                            {t('clubMember.preview.applications.family')}
                                        </Badge>
                                    )}
                                    {application.source === 'headless' && (
                                        <Badge flat size="xs" color="light">
                                            {t('clubMember.preview.applications.headless')}
                                        </Badge>
                                    )}
                                    {/* Accepting builds a member out of the application, it never writes
                                        into the one whose tab this is - worth saying where the two sit
                                        next to each other. */}
                                    {canRespond && application.status === 'requested' && (
                                        <span className="tw-text-xs tw-text-accents-6">
                                            {t('clubMember.preview.applications.newMemberHint')}
                                        </span>
                                    )}
                                    {application.match === 'related' && (
                                        <>
                                            <Badge flat size="xs" color="warning">
                                                {t('clubMember.preview.applications.guessed')}
                                            </Badge>
                                            <InfoTooltip
                                                tooltip={t('clubMember.preview.applications.guessedHint')}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            <TableActionButton.Container justify="flex-end">
                                {canRespond && application.status === 'requested' && (
                                    <>
                                        <TableActionButton
                                            icon={faCheck}
                                            tooltip={t('applications.list.acceptTooltip')}
                                            onPress={() => showResponseForm(application, 'accept')}
                                        />
                                        <TableActionButton
                                            icon={faTimes}
                                            color="error"
                                            tooltip={t('applications.list.declineTooltip')}
                                            onPress={() => showResponseForm(application, 'decline')}
                                        />
                                    </>
                                )}
                                {application.form_data_id && (
                                    <TableActionButton
                                        icon={faDatabase}
                                        color="light"
                                        tooltip={t('applications.list.relatedDataTooltip')}
                                        onPress={() => showFormDataPreview(application)}
                                    />
                                )}
                            </TableActionButton.Container>
                        </div>
                    ))}
                </div>
            )}
        </Loader>
    );
};

export default ClubMemberApplications;
